import { Edit02Icon as EditIcon, Trash01Icon as TrashIcon, ClockIcon, PhoneIcon } from '@untitledui-icons/react/line'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { getWeekdayName } from '@/lib/dateFormat'
import { getLocale } from '@/i18n'

export interface WorkingHoursRule {
  mode: 'client_first' | 'client_only'
  recurring: boolean
  recurrence_pattern?: {
    days: number[]
    start_time: string
    end_time: string
  }
  start_date?: string
  end_date?: string
  start_time?: string
  end_time?: string
  forward_number: string
  dial_timeout: number
  caller_id_mode?: 'caller' | 'assistant'
}

export interface WorkingHoursCardProps {
  rule: WorkingHoursRule
  index: number
  onEdit: () => void
  onDelete: () => void
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return dateStr
  }
}

export function WorkingHoursCard({ rule, onEdit, onDelete }: WorkingHoursCardProps) {
  const { t } = useTranslation()

  const getSummary = (): string => {
    if (rule.recurring && rule.recurrence_pattern) {
      const days = rule.recurrence_pattern.days
        .sort((a, b) => a - b)
        .map(day => getWeekdayName(day, 'short'))
        .join(', ')
      return `${days} • ${rule.recurrence_pattern.start_time} - ${rule.recurrence_pattern.end_time}`
    } else if (rule.start_date && rule.end_date) {
      return `${formatDate(rule.start_date)} ${t('resources.assistants.working_hours.date_to')} ${formatDate(rule.end_date)}`
    }
    return t('resources.assistants.working_hours.no_schedule')
  }

  return (
    <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="min-w-0 flex-1">
        <div className="mb-2">
          <Badge color="primary" size="sm">
            {t(`resources.assistants.working_hours.mode_${rule.mode}`)}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <ClockIcon className="h-4 w-4" />
          <span>{getSummary()}</span>
        </div>
        {rule.forward_number && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.working_hours.forward_to')}: {rule.forward_number}
          </p>
        )}
        {rule.caller_id_mode === 'assistant' && (
          <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <PhoneIcon className="h-3.5 w-3.5" />
            <span>{t('resources.assistants.answer_schedule.caller_id_assistant_label')}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button color="tertiary" size="sm" onPress={onEdit} className="p-2" iconLeading={EditIcon} />
        <Button color="tertiary" size="sm" onPress={onDelete} className="p-2 text-error-600 hover:text-error-700" iconLeading={TrashIcon} />
      </div>
    </div>
  )
}

export default WorkingHoursCard
